// Simple event emitter implementation
type Listener<T> = (payload?: T) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class EventEmitter<EventMap extends Record<string, any>> {
  private listeners: { [K in keyof EventMap]?: Listener<EventMap[K]>[] } = {};

  on<K extends keyof EventMap>(event: K, listener: Listener<EventMap[K]>): () => void {
    this.listeners[event] = this.listeners[event] || [];
    this.listeners[event]?.push(listener);

    // Return an unsubscribe function
    return () => this.off(event, listener);
  }

  off<K extends keyof EventMap>(event: K, listener: Listener<EventMap[K]>): void {
    if (!this.listeners[event]) return;

    this.listeners[event] = this.listeners[event]?.filter((l) => l !== listener);
  }

  emit<K extends keyof EventMap>(event: K, payload?: EventMap[K]): void {
    if (!this.listeners[event]) return;

    // Execute listeners asynchronously to avoid blocking the emitter
    setTimeout(() => {
      this.listeners[event]?.forEach((listener) => listener(payload));
    }, 0);
  }
}

// Define the specific events for this application
interface AppEvents {
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  maintenanceModeDetected: void; // No payload needed for this event
  // Example: userLoggedOut: { reason: string };
}

// Create and export a singleton instance for application-wide use
export const appEmitter = new EventEmitter<AppEvents>();
